/**
 * Property detail right sidebar — quick-glance summary cards rendered
 * to the right of the main tabbed content per V2 handoff §4.2.
 *
 * Cards: Property summary · Property details · KeyNest
 */
import { AlertTriangle, Building, Building2, KeyRound } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { effectiveApartmentOwner } from "@/lib/owner-metrics";
import {
  apartmentRepresentorName,
  summarizeApartmentConfig,
  usePropertyOccupiedRoomIds,
} from "@/hooks/v2/useProperties";
import { useLessors } from "@/hooks/v2/useLessors";
import { useLessorIdByOwnerUser } from "@/hooks/v2/owner-user-lessor";
import { usePropertyRepresentorGaps } from "@/hooks/v2/usePropertyRepresentorGaps";
import { useAuth } from "@/contexts/AuthContext";
import type { ApartmentListItem, Property, Room } from "@/types/property";
import type { Lessor } from "@/types/lessor";
import { useLanguage } from "@/contexts/LanguageContext";
import { useOperatorBranding } from "@/hooks/useOperatorBranding";
import { useMemo } from "react";

const REVENUE_MODEL_SHORT: Record<string, string> = {
  full_rent: "Full rent",
  pct_of_rent: "% of rent",
  flat_fee_per_room: "Flat fee/room",
};

type Props = {
  property: Property;
  rooms: Room[];
  apartments?: ApartmentListItem[];
  propertyLessor?: Lessor | null;
};

export function PropertySidebar({
  property,
  rooms,
  apartments = [],
  propertyLessor = null,
}: Props) {
  const { t } = useLanguage();
  // Operators see their own company name, never "CoLiviq" (COL-352 Phase 5).
  const { platformName } = useOperatorBranding();
  const { data: lessors = [] } = useLessors();
  const { data: lessorIdByOwnerUser = {} } = useLessorIdByOwnerUser();
  const lessorById = useMemo(
    () => new Map(lessors.map((l) => [l.id, l])),
    [lessors]
  );
  const visibleRooms = rooms.filter((r) => !r.is_hidden);
  // Bookings, not the stale rooms.status/available flags — same rule as the
  // room list and the apartment cards.
  const { data: occupiedRoomIds = new Set<number>() } =
    usePropertyOccupiedRoomIds(property.id);
  const occupied = visibleRooms.filter((r) =>
    occupiedRoomIds.has(Number(r.id))
  ).length;

  // Apartment-layer roll-up (COL-225). Rooms/occupancy here are summed from
  // the apartment cards so the numbers line up with the Apartments tab.
  const aptRooms = apartments.reduce((n, a) => n + a.rooms_count, 0);
  const aptOccupied = apartments.reduce((n, a) => n + a.occupied_count, 0);
  // Distinct owners by EFFECTIVE lessor — an apartment with no lessor of its
  // own belongs to the property's, so counting a.lessor_id alone under-counted
  // (and reported 0 owners for a property whose apartments all inherit).
  //
  // No owner-ACCOUNT argument here on purpose: apartments loaded for this card
  // don't carry owner_user_id, and they don't need to — the
  // apartments_fill_lessor_from_owner trigger keeps lessor_id canonical, so an
  // account-owned apartment already reports its lessor. If that trigger ever
  // goes, this count silently drops those apartments. (An owner account that
  // maps to SEVERAL lessors is left unfilled by design and stays uncounted —
  // that gap is the point: nobody should guess who owns it.)
  const owners = new Set(
    apartments
      .map((a) => effectiveApartmentOwner(a.lessor_id, property.lessor_id))
      .filter(Boolean)
  ).size;
  const aptOccPct =
    aptRooms > 0 ? Math.round((aptOccupied / aptRooms) * 100) : 0;

  // Divergence summary (COL-225 §4.3): only show a single representative
  // revenue value when apartments are uniform; otherwise "Varies".
  const cfg = summarizeApartmentConfig(apartments, property, {
    lessorById,
    propertyLessor,
    lessorIdByOwnerUser,
  });
  const collectionModeShort: Record<string, string> = {
    company_collects: `${platformName} collects`,
    owner_collects: "Owner collects",
  };
  const collectionVal = cfg.collectionMode.uniform
    ? (collectionModeShort[
        apartments[0]?.collection_mode ?? property.collection_mode
      ] ?? "—")
    : `Varies (${cfg.collectionMode.distinct})`;
  // Source of truth for the revenue model: apartment-level when uniform,
  // otherwise the property default (COL-267).
  const effectiveRevenueModel = cfg.revenueModel.uniform
    ? (apartments[0]?.revenue_model ?? property.revenue_model)
    : null;
  const revenueVal = cfg.revenueModel.uniform
    ? (REVENUE_MODEL_SHORT[effectiveRevenueModel ?? ""] ?? "—")
    : `Varies (${cfg.revenueModel.distinct})`;
  // Representor: the apartment/room-level effective representor when every
  // apartment agrees (which normally just echoes the property's own
  // representor), otherwise "Various" — same granularity as the Owners row.
  // Guarded on apartments.length: this card only renders with apartments
  // (see below), but `cfg.representor` still reports "uniform" for an empty
  // list (nothing to disagree on) and apartments[0] would then be undefined.
  // A "Various" or "—" can hide apartments whose contracts would print no
  // "vertreten durch" at all, so those get called out on their own (COL-466).
  // Staff-only, like the rest of the representor requirement.
  const { isOwnerPortalOnly } = useAuth();
  const repGaps = usePropertyRepresentorGaps(property, {
    enabled: !isOwnerPortalOnly,
  });
  // Once resolved, the gap hook's names are what the contracts print (the
  // property-scoped owner links, the legacy column), so the row shows those
  // rather than a second resolver that can disagree with the flag beside it.
  // Until then — or for a viewer it can't answer for — the summary below.
  const resolvedRepNames = repGaps.names
    ? [...new Set(repGaps.names.values())]
    : null;
  const representorVal =
    apartments.length === 0
      ? "—"
      : resolvedRepNames
        ? resolvedRepNames.length > 1
          ? t("admin.field.representorVarious", "Various")
          : (resolvedRepNames[0] ?? "—")
        : cfg.representor.uniform
          ? (apartmentRepresentorName(
              apartments[0],
              property,
              lessorById,
              propertyLessor,
              lessorIdByOwnerUser
            ) ?? "—")
          : t("admin.field.representorVarious", "Various");

  return (
    <div className="space-y-3">
      {apartments.length > 0 && (
        <SidebarCard
          icon={Building}
          title={t("admin.sidebar.propertySummary", "Property summary")}
        >
          <Row
            label={t("admin.field.apartments", "Apartments")}
            value={`${apartments.length}`}
          />
          <Row
            label={t("admin.section.rooms", "Rooms")}
            value={`${aptRooms} total`}
          />
          <Row
            label={t("admin.field.owners", "Owners")}
            value={`${owners} unique`}
          />
          <Row
            label={t("admin.field.occupancy", "Occupancy")}
            value={`${aptOccPct}%`}
          />
          <div className="mt-2 border-t pt-2 space-y-1">
            <Row
              label={t("admin.field.rentCollection", "Rent collection")}
              value={collectionVal}
            />
            <Row
              label={t("admin.field.revenueModel", "Revenue model")}
              value={revenueVal}
            />
            <Row
              label={t("admin.field.representor", "Representor")}
              value={
                repGaps.missing.length > 0 ? (
                  <span className="inline-flex items-start gap-1 text-amber-700 dark:text-amber-400">
                    <AlertTriangle
                      className="mt-0.5 h-3.5 w-3.5 shrink-0"
                      aria-hidden
                    />
                    {t(
                      "admin.field.representorMissingOn",
                      "Missing on {{count}} of {{total}}"
                    )
                      .replace("{{count}}", String(repGaps.missing.length))
                      .replace("{{total}}", String(repGaps.total))}
                  </span>
                ) : (
                  representorVal
                )
              }
            />
          </div>
        </SidebarCard>
      )}

      <SidebarCard
        icon={Building2}
        title={t("admin.sidebar.propertyDetails", "Property details")}
      >
        <Row label={t("admin.address", "Address")} value={property.address} />
        <Row
          label={t("admin.field.postalCode", "Postal code")}
          value={(property as { zip_code?: string | null }).zip_code || "—"}
        />
        <Row label={t("admin.city", "City")} value={property.city} />
        {property.type && (
          <Row label={t("admin.field.type", "Type")} value={property.type} />
        )}
        <Row
          label={t("admin.section.rooms", "Rooms")}
          value={
            visibleRooms.length === 0
              ? "—"
              : `${visibleRooms.length} (${occupied} ${t("admin.field.occupied", "occupied").toLowerCase()})`
          }
        />
        {property.is_hidden && (
          <div className="pt-1">
            <Badge variant="secondary">
              {t("admin.field.hidden", "Hidden from public listing")}
            </Badge>
          </div>
        )}
      </SidebarCard>

      <SidebarCard
        icon={KeyRound}
        title={t("admin.sidebar.keynest", "KeyNest")}
      >
        {property.keynest_pickup_location ||
        property.keynest_pickup_address ||
        property.keynest_access_url ||
        property.pickup_instructions ? (
          <>
            <Row
              label={t("admin.field.pickupMethod", "Method")}
              value={
                property.pickup_method === "manual"
                  ? t("admin.field.pickupManual", "Manual address")
                  : t("admin.field.pickupKeynest", "KeyNest")
              }
            />
            {property.keynest_pickup_location && (
              <Row
                label={t("admin.field.keynestLocation", "Location")}
                value={property.keynest_pickup_location}
              />
            )}
            {property.keynest_pickup_address && (
              <Row
                label={t("admin.address", "Address")}
                value={property.keynest_pickup_address}
              />
            )}
            {property.pickup_method !== "manual" &&
              property.keynest_access_url && (
                <Row
                  label={t("admin.field.keynestLink", "Link")}
                  value={
                    <a
                      href={property.keynest_access_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline break-all"
                    >
                      {property.keynest_access_url}
                    </a>
                  }
                />
              )}
            {property.pickup_instructions && (
              <Row
                label={t("admin.field.pickupInstructions", "Instructions")}
                value={property.pickup_instructions}
              />
            )}
            <p className="text-[11px] text-muted-foreground pt-1">
              {t(
                "admin.sidebar.keynestPropertyHint",
                "Property default — apartments and rooms inherit unless overridden. The access link is set per room."
              )}
            </p>
          </>
        ) : (
          <p className="text-xs text-muted-foreground">
            {t(
              "admin.sidebar.keynestHint",
              "KeyNest codes are stored per room. Open a room to view or edit them."
            )}
          </p>
        )}
      </SidebarCard>
    </div>
  );
}

function SidebarCard({
  icon: Icon,
  title,
  children,
}: {
  icon: typeof Building2;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <Card className="p-4 space-y-2">
      <header className="flex items-center gap-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
        <Icon className="h-3.5 w-3.5" />
        {title}
      </header>
      <div className="space-y-1.5 text-sm">{children}</div>
    </Card>
  );
}

function Row({
  label,
  value,
  mono,
}: {
  label: string;
  value: React.ReactNode;
  mono?: boolean;
}) {
  return (
    <div className="flex items-baseline gap-3">
      <span className="text-xs text-muted-foreground shrink-0 min-w-[80px]">
        {label}
      </span>
      <span
        className={`text-sm flex-1 min-w-0 break-words [overflow-wrap:anywhere] ${mono ? "font-mono text-xs" : ""}`}
      >
        {value}
      </span>
    </div>
  );
}
